import { Button, Card, Col, ProgressBar, Row, Table } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { useEffect, useState } from "react";
import { Problem } from "../api/backendApi";
import {
  ModelInfoYearEndModel,
  YearlyOverviewVehicleFileDetailsViewModel,
  yearlyReviewApi,
} from "../api/yearlyReviewApi";
import ListDisplay from "../components/lists/ListDisplay";
import { useRoutablePages } from "../common/RoutablePages";
import {
  vehicleFileAPI,
  VehicleFileValidationTypeModel,
} from "../api/vehicleFileAPI";

function YearlyOverviewDetailsPage() {
  const location = useLocation();
  const routes = useRoutablePages();

  const [yearlyOverviewVehicleFile, setYearlyOverviewVehicleFile] =
    useState<YearlyOverviewVehicleFileDetailsViewModel | null>(null);
  const [uniqueTableKeys, setUniqueTableKeys] = useState<string[]>([]);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const vehicleKeys: { [key: string]: string } = {
    region: "Region",
    model: "Model",
    modelTypes: "Model Types",
    modelVariants: "Model Variants",
    modelInfo: "Model Info",
    extraModelInfo: "Extra Model Info",
    fuelType: "Fuel Type",
    hybridTypeName: "Hybrid",
    cylinderCount: "Cylinder Count",
    engineSize: "Engine Size",
    engineCodes: "Engine Codes",
    drivetrainMaximumKws: "Drivetrain Maximum Kws",
    drivetrainNominalKws: "Drivetrain Nominal Kws",
    engineMaximumKws: "Engine Maximum Kws",
    engineNominalKws: "Engine Nominal Kws",
    engineFrontAxle1MaximumKws: "Front Axle 1 Maximum Kws",
    engineFrontAxle1NominalKws: "Front Axle 1 Nominal Kws",
    engineFrontAxle2MaximumKws: "Front Axle 2 Maximum Kws",
    engineFrontAxle2NominalKws: "Front Axle 2 Nominal Kws",
    engineRearAxle1MaximumKws: "Rear Axle 1 Maximum Kws",
    engineRearAxle1NominalKws: "Rear Axle 1 Nominal Kws",
    engineRearAxle2MaximumKws: "Rear Axle 2 Maximum Kws",
    engineRearAxle2NominalKws: "Rear Axle 2 Nominal Kws",
    mildHybridMaximumKws: "Mild Hybrid Maximum Kws",
    mildHybridNominalKws: "Mild Hybrid Nominal Kws",
    electric1MaximumKws: "Electric 1 Maximum Kws",
    electric1NominalKws: "Electric 1 Nominal Kws",
    electric2MaximumKws: "Electric 2 Maximum Kws",
    electric2NominalKws: "Electric 2 Nominal Kws",
    electric3MaximumKws: "Electric 3 Maximum Kws",
    electric3NominalKws: "Electric 3 Nominal Kws",
    electric4MaximumKws: "Electric 4 Maximum Kws",
    electric4NominalKws: "Electric 4 Nominal Kws",
  };

  useEffect(() => {
    async function loadYearlyOverviewVehicleFileDetails() {
      setLoading(true);
      var data = await yearlyReviewApi.getYearlyOverviewVehicleFileDetails(
        location.state.makeCode,
        location.state.yearEnd,
      );

      if (data.ok) {
        setError(undefined);
        setYearlyOverviewVehicleFile(data.value);
        loadTableHeaders(data.value);
      } else {
        setError(data.problem);
        routes.toYearlyReviewList(
          location.state.makeCode,
          location.state.yearEnd,
        );
      }
      setLoading(false);
    }
    loadYearlyOverviewVehicleFileDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  function loadTableHeaders(
    yearlyOverviewVehicleFile: YearlyOverviewVehicleFileDetailsViewModel,
  ) {
    var yearlyOverviewVehicleFileKeys = Array.from(
      new Set(
        yearlyOverviewVehicleFile?.vehicles.flatMap((vehicle) => {
          var keys = Object.keys(vehicle).filter((key) => {
            if (vehicleKeys[key] !== undefined) {
              var value = vehicle[key];

              if (
                (value !== null && !Array.isArray(value)) ||
                (Array.isArray(value) && value.length > 0)
              ) {
                return key;
              }
            }
            return null;
          });
          return keys;
        }),
      ),
    );
    setUniqueTableKeys(yearlyOverviewVehicleFileKeys);
  }

  function isStringArray(value: any): value is string[] {
    return (
      Array.isArray(value) && value.every((item) => typeof item === "string")
    );
  }

  function isNumberArray(value: any): value is number[] {
    return (
      Array.isArray(value) && value.every((item) => typeof item === "number")
    );
  }

  function processVehicleKey(
    vehicle: { [key: string]: any },
    key: string,
  ): string[] | number[] {
    const value = vehicle[key];

    if (isStringArray(value)) {
      return value;
    } else if (isNumberArray(value)) {
      return value;
    }
    return [];
  }

  async function handleEditButton(
    yearlyOverviewVehicleFile: YearlyOverviewVehicleFileDetailsViewModel,
  ) {
    var response = await vehicleFileAPI.checkoutVehicleFile(
      yearlyOverviewVehicleFile.number,
    );

    if (response.ok) {
      setError(undefined);
      routes.toModelInfoEditForYearlyReview(
        yearlyOverviewVehicleFile.number,
        yearlyOverviewVehicleFile.make.code,
        location.state.yearEnd,
        location.state.yearEnd + 1,
        false,
      );
    } else {
      setError(response.problem);
      routes.toModelInfoToReview(
        location.state.makeCode,
        location.state.yearEnd,
      );
    }
  }

  async function handleReviewedButton(
    yearlyOverviewVehicleFile: YearlyOverviewVehicleFileDetailsViewModel,
  ) {
    await checkoutVehicleFile(yearlyOverviewVehicleFile.number);
    await updateModelInfo(
      yearlyOverviewVehicleFile.number,
      yearlyOverviewVehicleFile.vehicles,
    );
    await checkInVehicleFile(yearlyOverviewVehicleFile.number);
  }

  async function checkoutVehicleFile(vehicleFileNumber: string) {
    var response = await vehicleFileAPI.checkoutVehicleFile(vehicleFileNumber);

    if (response.ok) {
      setError(undefined);
    } else {
      setError(response.problem);
      routes.toModelInfoToReview(
        location.state.makeCode,
        location.state.yearEnd,
      );
    }
  }

  async function updateModelInfo(
    vehicleFileNumber: string,
    modelInfos: ModelInfoYearEndModel[],
  ) {
    if (error === undefined) {
      modelInfos = modelInfos.map((modelInfo) => {
        modelInfo = { ...modelInfo, yearEnd: location.state.yearEnd + 1 };

        return modelInfo;
      });

      var response = await yearlyReviewApi.updateModelInfoYearEnd(
        vehicleFileNumber,
        modelInfos,
      );

      if (response.ok) {
        setError(undefined);
      } else {
        setError(response.problem);
      }
    }
  }

  async function checkInVehicleFile(vehicleFileNumber: string) {
    var response = await vehicleFileAPI.checkinVehicleFile(
      vehicleFileNumber,
      `Yearly review ${location.state.yearEnd}`,
      false,
    );

    if (response.ok) {
      setError(undefined);

      await vehicleFileAPI.setVehicleValidatedFlag(
        vehicleFileNumber,
        VehicleFileValidationTypeModel.ModelInfo,
        true,
      );
      await vehicleFileAPI.setVehicleValidatedFlag(
        vehicleFileNumber,
        VehicleFileValidationTypeModel.HelperFilesValidation,
        true,
      );

      routes.toModelInfoToReview(
        location.state.makeCode,
        location.state.yearEnd,
      );
    } else {
      setError(response.problem);
    }
  }

  return (
    <>
      <div className="main-container-title">
        <h1>Review Vehicle</h1>
        <ErrorDisplay error={error} />

        <LoadingDisplay loading={loading} />
      </div>
      <div
        className="main-container-content"
        style={{ overflowY: "auto", overflowX: "auto" }}
      >
        {!loading && yearlyOverviewVehicleFile && (
          <Card>
            <Card.Header>
              <Row>
                <Col md="auto" style={{ fontSize: 18, fontWeight: 400 }}>
                  {yearlyOverviewVehicleFile?.number}
                </Col>
                <Col style={{ fontSize: 18, fontWeight: 400 }}>
                  {yearlyOverviewVehicleFile?.description}
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    {Object.keys(vehicleKeys).map((key) =>
                      uniqueTableKeys.includes(key) ? (
                        <th key={key}>{vehicleKeys[key]}</th>
                      ) : null,
                    )}
                    <th>New End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyOverviewVehicleFile
                    ? yearlyOverviewVehicleFile.vehicles.map((vehicle) => (
                        <tr key={vehicle.recId}>
                          {Object.keys(vehicleKeys).map((key) =>
                            uniqueTableKeys.includes(key) ? (
                              <td key={key} style={{ maxWidth: "250px" }}>
                                {Array.isArray(vehicle[key]) ? (
                                  <ListDisplay
                                    initialList={processVehicleKey(
                                      vehicle,
                                      key,
                                    )}
                                    alternativeValue="Empty"
                                  />
                                ) : (
                                  vehicle[key]
                                )}
                              </td>
                            ) : null,
                          )}
                          <td>
                            {vehicle.monthEnd ? vehicle.monthEnd : "End"}{" "}
                            {vehicle.yearEnd + 1}
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer>
              <Row>
                <Col
                  style={{
                    fontSize: 18,
                    fontWeight: 400,
                    marginTop: "auto",
                    marginBottom: "auto",
                  }}
                >
                  {yearlyOverviewVehicleFile?.make.name}
                </Col>
                <Col
                  style={{
                    fontSize: 18,
                    fontWeight: 400,
                    marginTop: "auto",
                    marginBottom: "auto",
                    maxWidth: "150px",
                  }}
                >
                  <Col>
                    <ProgressBar
                      style={{ height: "2px" }}
                      variant="info"
                      now={
                        (1 /
                          yearlyOverviewVehicleFile.remainingVehiclesForReview) *
                        100
                      }
                    />
                  </Col>
                  <Col style={{ textAlign: "center" }}>
                    {yearlyOverviewVehicleFile.remainingVehiclesForReview}
                  </Col>
                </Col>
                <Col>
                  <div style={{ marginLeft: "auto", width: "fit-content" }}>
                    <Button
                      onClick={async () => {
                        handleEditButton(yearlyOverviewVehicleFile);
                      }}
                      variant="primary"
                    >
                      Edit
                    </Button>
                    <Button
                      className="ms-3"
                      onClick={async () => {
                        handleReviewedButton(yearlyOverviewVehicleFile);
                      }}
                      variant="success"
                    >
                      Update
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        )}
      </div>
    </>
  );
}

export default YearlyOverviewDetailsPage;
